import { deriveFieldValidations, type DerivedValidation } from '@capture/shared';
import type { JobTypeVersionContentV3 } from '@capture/shared';

export interface DerivedCheckRow {
  formId: string;
  formName: string;
  fieldLabel: string;
  sourceLabel: string;
  summary: string;
}

/** One-line summary of a derived validation rule for the read-only list. */
export function summarizeDerivedRule(rule: DerivedValidation['rule']): string {
  switch (rule.type) {
    case 'required': return 'must be filled';
    case 'pattern': return rule.pattern ?? '(pattern)';
    case 'length': {
      const parts: string[] = [];
      if (typeof rule.min === 'number') parts.push(`min ${rule.min}`);
      if (typeof rule.max === 'number') parts.push(`max ${rule.max}`);
      return parts.length ? parts.join(', ') : 'length';
    }
    case 'compare': return `${rule.op === 'lte' ? '≤' : '≥'} ${rule.value ?? ''}`.trim();
    default: return '';
  }
}

/** Intrinsic field-derived checks across all forms — display-only (authored on the Fields tab). */
export function collectDerivedChecks(contentV3: JobTypeVersionContentV3 | null): DerivedCheckRow[] {
  const out: DerivedCheckRow[] = [];
  for (const form of contentV3?.forms ?? []) {
    for (const field of form.fields ?? []) {
      for (const d of deriveFieldValidations(field)) {
        out.push({
          formId: form.id, formName: form.name,
          fieldLabel: field.label || field.name,
          sourceLabel: d.sourceLabel,
          summary: summarizeDerivedRule(d.rule),
        });
      }
    }
  }
  return out;
}
